/**
 * components/admin/DepositReconciliationPanel.jsx · v2.7.56+E33
 * ═══════════════════════════════════════════════════════════════════
 * Deposit reconciliation surface for supervisors / operating-partners /
 * admins. Renders every driver-submitted End-of-Day deposit with a
 * verify field and, on mismatch, transitions the row into ESCALATED
 * with a drawer for internal comments + ticket creation.
 *
 * Reads:
 *   GET  /api/admin/deposits?status=&from=&to=&q=
 *   GET  /api/admin/deposits/{id}
 * Writes (supervisor+ only):
 *   POST /api/admin/deposits/{id}/verify   { verified_deposit_amount, note? }
 *   POST /api/admin/deposits/{id}/comment  { text }
 *   POST /api/admin/deposits/{id}/escalate { note?, priority? }
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import adminApi from "@/lib/adminApi";
import { fmtUsd } from "@/lib/format";
import { toast } from "sonner";
import DepositRollupCard from "@/components/admin/DepositRollupCard";
import {
  Wallet, CheckCircle2, AlertTriangle, MessageSquare, Filter, RefreshCw,
  Ticket as TicketIcon, ExternalLink, FileImage, HandCoins, Search,
  Landmark, TrendingUp, TrendingDown, MinusCircle, ShieldCheck,
} from "lucide-react";

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "PENDING", label: "Pending", tone: "amber" },
  { key: "ESCALATED", label: "Escalated", tone: "red" },
  { key: "CLOSED", label: "Closed", tone: "emerald" },
];

export default function DepositReconciliationPanel() {
  const [tab, setTab] = useState("PENDING");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const activeRow = useMemo(() => items.find((i) => i.id === activeId) || null, [items, activeId]);

  const load = useCallback(async (opts = {}) => {
    const isRefresh = !!opts.refresh;
    isRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab !== "all") params.set("status", tab);
      if (q) params.set("q", q);
      params.set("limit", "500");
      const r = await adminApi.get(`/admin/deposits?${params}`);
      setItems(r.data?.items || []);
      setStats(r.data?.stats || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to load deposits");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [tab, q]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      {/* v2.7.56+E33 · 7/30/90-day per-driver rollup — surfaces repeat-
          discrepancy drivers at the top of the earnings surface so ops
          reviewers can spot patterns in a single scan. */}
      <DepositRollupCard/>
      <Card className="bg-zinc-900 border-zinc-800 p-4 mb-6" data-testid="deposit-recon-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center">
            <Wallet className="h-4.5 w-4.5 text-emerald-300"/>
          </div>
          <div>
            <div className="text-sm font-bold text-zinc-100">Driver Deposits · Reconciliation</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest">Verify · Close · Escalate</div>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => load({ refresh: true })} disabled={refreshing} className="h-8 text-xs" data-testid="deposit-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`}/>Refresh
        </Button>
      </div>

      {/* Stats banner */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
        <MiniStat label="Rows" value={stats.total_rows ?? 0} testid="deposit-stat-rows"/>
        <MiniStat label="Pending" value={stats.pending ?? 0} tone="amber" testid="deposit-stat-pending"/>
        <MiniStat label="Escalated" value={stats.escalated ?? 0} tone="red" testid="deposit-stat-escalated"/>
        <MiniStat label="Submitted $" value={fmtUsd(stats.total_submitted ?? 0)} testid="deposit-stat-submitted"/>
        <MiniStat label="Tips $" value={fmtUsd(stats.total_tips ?? 0)} testid="deposit-stat-tips"/>
      </div>

      {/* Filter tabs + search */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-2.5 py-1 rounded text-[11px] uppercase font-bold tracking-wider border transition ${
              tab === t.key
                ? "bg-amber-400 border-amber-400 text-zinc-950"
                : "bg-zinc-950 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            }`}
            data-testid={`deposit-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
        <div className="flex-1 min-w-[200px] relative">
          <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none"/>
          <Input
            placeholder="Search driver / memo…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="bg-zinc-950 border-zinc-700 h-8 pl-7 text-xs"
            data-testid="deposit-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-center text-zinc-500 text-sm" data-testid="deposit-loading">Loading deposits…</div>
      ) : items.length === 0 ? (
        <div className="p-6 text-center text-zinc-500 text-sm border border-dashed border-zinc-800 rounded" data-testid="deposit-empty">
          No deposits match the current filter.
        </div>
      ) : (
        <div className="overflow-x-auto -mx-2" data-testid="deposit-table-wrap">
          <table className="w-full text-xs" data-testid="deposit-table">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-2 py-2">Driver</th>
                <th className="text-left px-2 py-2">Shift Date</th>
                <th className="text-right px-2 py-2">Cash</th>
                <th className="text-right px-2 py-2">Square</th>
                <th className="text-right px-2 py-2">Cash App</th>
                <th className="text-right px-2 py-2">Venmo</th>
                <th className="text-right px-2 py-2">Tips</th>
                <th className="text-right px-2 py-2">Submitted</th>
                <th className="text-center px-2 py-2">Receipt</th>
                <th className="text-center px-2 py-2">Status</th>
                <th className="text-center px-2 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <DepositRow
                  key={row.id}
                  row={row}
                  onOpen={() => setActiveId(row.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Drawer / dialog for the active deposit */}
      <Dialog open={!!activeRow} onOpenChange={(o) => { if (!o) setActiveId(null); }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[85vh] overflow-y-auto">
          {activeRow && (
            <DepositDrawer
              row={activeRow}
              onUpdated={(updated) => {
                setItems((list) => list.map((i) => i.id === updated.id ? updated : i));
              }}
              onClose={() => setActiveId(null)}
              reload={() => load({ refresh: true })}
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
    </>
  );
}


function DepositRow({ row, onOpen }) {
  const status = row.status || "PENDING";
  const chip = {
    PENDING:   "bg-zinc-800 text-zinc-300 border-zinc-600",
    CLOSED:    "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
    ESCALATED: "bg-red-500/15 text-red-300 border-red-400/40",
  }[status];
  const icon = status === "CLOSED" ? <CheckCircle2 className="h-3 w-3"/>
    : status === "ESCALATED" ? <AlertTriangle className="h-3 w-3"/>
    : null;
  return (
    <tr className="border-t border-zinc-800 hover:bg-zinc-800/30" data-testid={`deposit-row-${row.id}`}>
      <td className="px-2 py-2 font-semibold">{row.driver_name || row.driver_email?.split("@")[0]}</td>
      <td className="px-2 py-2 text-zinc-400">{row.shift_date}</td>
      <td className="px-2 py-2 text-right meter-display">{fmtUsd(row.cash_amount)}</td>
      <td className="px-2 py-2 text-right meter-display">{fmtUsd(row.square_amount)}</td>
      <td className="px-2 py-2 text-right meter-display">{fmtUsd(row.cash_app_amount)}</td>
      <td className="px-2 py-2 text-right meter-display">{fmtUsd(row.venmo_amount)}</td>
      <td className="px-2 py-2 text-right meter-display text-amber-300">{fmtUsd(row.tips_amount)}</td>
      <td className="px-2 py-2 text-right meter-display font-bold">{fmtUsd(row.total_deposit_submitted)}</td>
      <td className="px-2 py-2 text-center">
        {row.receipt_url ? (
          <FileImage className="h-3.5 w-3.5 mx-auto text-sky-300" title={row.receipt_url}/>
        ) : (
          <span className="text-zinc-600 text-[10px]">—</span>
        )}
      </td>
      <td className="px-2 py-2 text-center">
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${chip}`} data-testid={`deposit-status-${row.id}`}>
          {icon}{status}
        </span>
      </td>
      <td className="px-2 py-2 text-center">
        <Button size="sm" variant="outline" onClick={onOpen} className="h-7 text-[10px]" data-testid={`deposit-open-${row.id}`}>Open</Button>
      </td>
    </tr>
  );
}


function DepositDrawer({ row, onUpdated, onClose, reload }) {
  const [verifyValue, setVerifyValue] = useState(
    row.verified_deposit_amount != null ? String(row.verified_deposit_amount) : ""
  );
  const [verifyNote, setVerifyNote] = useState("");
  const [commentText, setCommentText] = useState("");
  const [escalateNote, setEscalateNote] = useState("");
  const [busy, setBusy] = useState(false);

  const closed = row.status === "CLOSED";

  const doVerify = async () => {
    const v = parseFloat(verifyValue);
    if (!(v >= 0)) {
      toast.error("Enter a non-negative verified amount.");
      return;
    }
    setBusy(true);
    try {
      const r = await adminApi.post(`/admin/deposits/${row.id}/verify`, {
        verified_deposit_amount: v,
        note: verifyNote || null,
      });
      const status = r.data?.status;
      const dsc = r.data?.discrepancy;
      if (status === "CLOSED") {
        toast.success(`Deposit CLOSED · exact match ($${dsc.toFixed(2)})`);
      } else {
        toast.warning(`ESCALATED · variance $${dsc.toFixed(2)} · driver + supervisors notified`);
      }
      if (r.data?.deposit) onUpdated(r.data.deposit);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Verify failed");
    } finally {
      setBusy(false);
    }
  };

  const doComment = async () => {
    const t = commentText.trim();
    if (!t) return;
    setBusy(true);
    try {
      const r = await adminApi.post(`/admin/deposits/${row.id}/comment`, { text: t });
      toast.success("Comment appended");
      setCommentText("");
      // Optimistic append
      onUpdated({ ...row, comments: [...(row.comments || []), r.data.comment] });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Comment failed");
    } finally {
      setBusy(false);
    }
  };

  const doEscalate = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post(`/admin/deposits/${row.id}/escalate`, {
        note: escalateNote || null,
        priority: "high",
      });
      if (r.data?.already_escalated) {
        toast.info(`Ticket already exists · ${r.data.escalation_ticket_no}`);
      } else {
        toast.success(`Ticket created · ${r.data.escalation_ticket_no}`);
      }
      setEscalateNote("");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Escalation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-emerald-300"/>
          Deposit · {row.driver_name || row.driver_email}
          <Badge variant="outline" className={`ml-auto text-[10px] ${row.status === "ESCALATED" ? "border-red-400 text-red-300" : row.status === "CLOSED" ? "border-emerald-400 text-emerald-300" : "border-zinc-500 text-zinc-300"}`} data-testid="drawer-status">
            {row.status}
          </Badge>
        </DialogTitle>
      </DialogHeader>

      <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
        <Field label="Shift Date" value={row.shift_date}/>
        <Field label="Submitted At" value={row.created_at?.slice(0, 19).replace("T", " ")}/>
        <Field label="Cash" value={fmtUsd(row.cash_amount)}/>
        <Field label="Square" value={fmtUsd(row.square_amount)}/>
        <Field label="Cash App" value={fmtUsd(row.cash_app_amount)}/>
        <Field label="Venmo" value={fmtUsd(row.venmo_amount)}/>
        <Field label="Tips" value={fmtUsd(row.tips_amount)} accent="amber"/>
        <Field label="Total Submitted" value={fmtUsd(row.total_deposit_submitted)} accent="emerald"/>
      </div>

      {/* v2.7.63+E46.3 · Compliance Breakdown — Tips vs Profits split
          mirrors the driver-side EOD card so admins/dispatchers see the
          exact same ledger view during bank reconciliation. */}
      <div
        className="mt-3 p-3 rounded-md border border-sky-400/40 bg-sky-500/5"
        data-testid="drawer-compliance-card"
      >
        <div className="text-[10px] uppercase tracking-widest text-sky-200 font-bold mb-1.5">
          Compliance Breakdown · Bank Reconciliation
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="p-2 rounded bg-sky-500/10 border border-sky-400/40" data-testid="drawer-compliance-total-tile">
            <div className="text-[9px] uppercase tracking-widest text-sky-300 opacity-80">Total Expected</div>
            <div className="meter-display text-base font-extrabold text-sky-200 mt-0.5" data-testid="drawer-compliance-total">
              {fmtUsd((row.tips_amount || 0) + (row.total_deposit_submitted || 0))}
            </div>
          </div>
          <div className="p-2 rounded bg-amber-500/10 border border-amber-400/40" data-testid="drawer-compliance-tips-tile">
            <div className="text-[9px] uppercase tracking-widest text-amber-300 opacity-80">Tips</div>
            <div className="meter-display text-base font-extrabold text-amber-200 mt-0.5" data-testid="drawer-compliance-tips">
              {fmtUsd(row.tips_amount || 0)}
            </div>
          </div>
          <div className="p-2 rounded bg-emerald-500/10 border border-emerald-400/40" data-testid="drawer-compliance-profits-tile">
            <div className="text-[9px] uppercase tracking-widest text-emerald-300 opacity-80">Profits</div>
            <div className="meter-display text-base font-extrabold text-emerald-200 mt-0.5" data-testid="drawer-compliance-profits">
              {fmtUsd(row.total_deposit_submitted || 0)}
            </div>
          </div>
        </div>
        <div className="text-[10px] text-zinc-500 mt-2">
          Cross-check the <span className="text-emerald-300 font-bold">Profits</span> column against
          the actual bank deposit for this shift date.
        </div>
      </div>

      {/* v2.7.63+E46.4 · Bank Reconciliation Diff — closes driver → admin
          → bank compliance loop. Renders Expected · Declared · Bank Actual
          · Variance with traffic-light thresholds ($2 green / $10 amber /
          $>$10 red) and an inline input for admin to record the actual
          bank statement amount. */}
      <BankReconciliationDiff deposit={row} onSaved={() => { reload && reload(); }}/>

      {row.memo && (
        <div className="mt-3 p-2 rounded bg-zinc-900 border border-zinc-800 text-xs" data-testid="drawer-memo">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Driver Memo</div>
          <div className="text-zinc-300 mt-1 whitespace-pre-wrap">{row.memo}</div>
        </div>
      )}
      {row.receipt_url && (
        <div className="mt-2 text-xs" data-testid="drawer-receipt">
          <span className="text-zinc-500">Receipt: </span>
          <span className="text-sky-300 font-mono">{row.receipt_url}</span>
        </div>
      )}

      {/* Verification block */}
      <div className="mt-4 border-t border-zinc-800 pt-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Verification</div>
        <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400">Verified Deposit Amount</Label>
            <div className="relative mt-1">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-amber-400 text-xs font-bold pointer-events-none">$</span>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={verifyValue}
                onChange={(e) => setVerifyValue(e.target.value)}
                disabled={closed}
                data-testid="drawer-verified-input"
                className="bg-zinc-950 border-zinc-700 h-10 pl-5 meter-display"
              />
            </div>
          </div>
          <Button
            onClick={doVerify}
            disabled={busy || closed || verifyValue === ""}
            className="h-10 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-extrabold"
            data-testid="drawer-verify-btn"
          >
            Close Deposit
          </Button>
        </div>
        {!closed && (
          <Textarea
            rows={2}
            value={verifyNote}
            onChange={(e) => setVerifyNote(e.target.value)}
            placeholder="Optional note attached to the verification (audit log)…"
            className="mt-2 bg-zinc-950 border-zinc-700 text-xs"
            data-testid="drawer-verify-note"
          />
        )}
        {row.status === "ESCALATED" && (
          <div className="mt-3 p-2 rounded border border-red-400/40 bg-red-500/10 text-xs" data-testid="drawer-discrepancy-banner">
            <div className="flex items-center gap-2 text-red-300 font-bold">
              <AlertTriangle className="h-3.5 w-3.5"/>Discrepancy locked · {fmtUsd(row.discrepancy_amount)}
            </div>
            <div className="text-red-200/80 mt-1">
              Driver auto-notified · admin comment thread + escalation ticket options below.
            </div>
          </div>
        )}
      </div>

      {/* Escalation actions (only surface when ESCALATED) */}
      {row.status === "ESCALATED" && (
        <div className="mt-4 border-t border-zinc-800 pt-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center gap-1">
            <TicketIcon className="h-3 w-3"/>Escalation
          </div>
          {row.escalation_ticket_no ? (
            <div className="p-2 rounded bg-sky-500/10 border border-sky-400/40 text-xs text-sky-200 flex items-center gap-2" data-testid="drawer-ticket-link">
              <TicketIcon className="h-3.5 w-3.5"/>
              Support ticket <span className="font-bold">{row.escalation_ticket_no}</span> created — check Helpdesk.
            </div>
          ) : (
            <>
              <Textarea
                rows={2}
                value={escalateNote}
                onChange={(e) => setEscalateNote(e.target.value)}
                placeholder="Priority reason (goes into the support ticket body)…"
                className="bg-zinc-950 border-zinc-700 text-xs"
                data-testid="drawer-escalate-note"
              />
              <Button
                onClick={doEscalate}
                disabled={busy}
                className="mt-2 w-full h-9 bg-red-500 hover:bg-red-400 text-white font-bold"
                data-testid="drawer-escalate-btn"
              >
                Priority-escalate to Supervisor + Operating Partner
              </Button>
            </>
          )}
        </div>
      )}

      {/* Comment thread */}
      <div className="mt-4 border-t border-zinc-800 pt-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
          <MessageSquare className="h-3 w-3"/>Internal Comments
        </div>
        {(row.comments || []).length === 0 && (
          <div className="text-[11px] text-zinc-600 italic mb-2" data-testid="drawer-no-comments">No comments yet.</div>
        )}
        <div className="space-y-1.5 max-h-32 overflow-y-auto" data-testid="drawer-comments">
          {(row.comments || []).map((c) => (
            <div key={c.id} className="p-2 rounded bg-zinc-900 border border-zinc-800 text-xs" data-testid={`drawer-comment-${c.id}`}>
              <div className="flex items-center justify-between text-[10px] text-zinc-500">
                <span>{c.by} <span className="uppercase text-zinc-600">· {c.by_role}</span></span>
                <span>{c.at?.slice(0, 19).replace("T", " ")}</span>
              </div>
              <div className="text-zinc-200 mt-1 whitespace-pre-wrap">{c.text}</div>
            </div>
          ))}
        </div>
        <div className="mt-2 flex gap-2">
          <Textarea
            rows={1}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Add a note (staff-visible)…"
            className="flex-1 bg-zinc-950 border-zinc-700 text-xs"
            data-testid="drawer-comment-input"
          />
          <Button
            onClick={doComment}
            disabled={busy || !commentText.trim()}
            className="h-9 self-end"
            data-testid="drawer-comment-btn"
          >
            Post
          </Button>
        </div>
      </div>
    </>
  );
}

function BankReconciliationDiff({ deposit, onSaved }) {
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bankAmt, setBankAmt] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!deposit?.id) return;
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/deposits/${deposit.id}/reconciliation-diff`);
      setDiff(r.data);
      if (r.data?.bank_statement_amount != null) {
        setBankAmt(String(r.data.bank_statement_amount));
      } else {
        setBankAmt("");
      }
      setNote(r.data?.bank_statement_note || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load reconciliation diff");
    } finally { setLoading(false); }
  }, [deposit?.id]);

  useEffect(() => { load(); }, [load]);

  const numBank = parseFloat(bankAmt);
  const canSave = Number.isFinite(numBank) && numBank >= 0;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const r = await adminApi.patch(`/admin/deposits/${deposit.id}/bank-statement`, {
        bank_statement_amount: numBank,
        note: note.trim() || null,
      });
      toast.success(`Bank statement recorded · ${r.data.reconciliation_status}`);
      await load();
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const statusMeta = {
    GREEN:  { tone: "bg-emerald-500/10 border-emerald-400/40 text-emerald-200", label: "WITHIN TOLERANCE", icon: <CheckCircle2 className="h-3.5 w-3.5"/> },
    AMBER:  { tone: "bg-amber-500/10 border-amber-400/40 text-amber-200",         label: "NEEDS REVIEW",      icon: <AlertTriangle className="h-3.5 w-3.5"/> },
    RED:    { tone: "bg-red-500/10 border-red-400/50 text-red-200",                label: "FLAGGED",           icon: <AlertTriangle className="h-3.5 w-3.5"/> },
    UNSET:  { tone: "bg-zinc-800 border-zinc-700 text-zinc-400",                   label: "NOT ENTERED",       icon: <MinusCircle className="h-3.5 w-3.5"/> },
  }[diff?.reconciliation_status || "UNSET"];

  const varianceRaw = diff?.variance_vs_ledger;
  const varianceTone = varianceRaw == null
    ? "text-zinc-400"
    : Math.abs(varianceRaw) <= (diff?.tolerance_green ?? 2)  ? "text-emerald-300"
    : Math.abs(varianceRaw) <= (diff?.tolerance_amber ?? 10) ? "text-amber-300"
    : "text-red-400";

  return (
    <div
      className="mt-3 p-3 rounded-md border border-purple-400/40 bg-gradient-to-br from-purple-500/5 to-zinc-950"
      data-testid="drawer-bank-recon-card"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5">
          <Landmark className="h-3.5 w-3.5 text-purple-300"/>
          <span className="text-[10px] uppercase tracking-widest text-purple-200 font-bold">
            Bank Reconciliation Diff · Driver → Admin → Bank
          </span>
        </div>
        {diff && (
          <div
            className={`text-[9px] uppercase tracking-widest font-extrabold px-2 py-0.5 rounded border flex items-center gap-1 ${statusMeta.tone}`}
            data-testid="drawer-bank-recon-status"
          >
            {statusMeta.icon}
            {statusMeta.label}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-[11px] text-zinc-500 italic p-2">Loading ledger diff…</div>
      ) : diff && (
        <>
          <div className="grid grid-cols-4 gap-2 text-xs">
            <MiniColumn
              label="Expected (Ledger)"
              value={fmtUsd(diff.expected_profits)}
              hint={`${diff.sources?.deliveries?.count ?? 0} deliv · ${diff.sources?.trips?.count ?? 0} trips`}
              tone="sky"
              testid="drawer-bank-expected"
              icon={<Landmark className="h-3 w-3"/>}
            />
            <MiniColumn
              label="Driver Declared"
              value={fmtUsd(diff.driver_declared)}
              hint="Submitted on EOD form"
              tone="amber"
              testid="drawer-bank-declared"
              icon={<HandCoins className="h-3 w-3"/>}
            />
            <MiniColumn
              label="Bank Actual"
              value={diff.bank_statement_amount != null ? fmtUsd(diff.bank_statement_amount) : "—"}
              hint={diff.bank_statement_recorded_at ?
                `${diff.bank_statement_recorded_by} · ${diff.bank_statement_recorded_at.slice(5,10)}` :
                "Enter below"}
              tone="purple"
              testid="drawer-bank-actual"
              icon={<Wallet className="h-3 w-3"/>}
            />
            <div className="p-2 rounded border border-zinc-700 bg-zinc-950" data-testid="drawer-bank-variance-tile">
              <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-zinc-500">
                {varianceRaw != null && varianceRaw >= 0 ?
                  <TrendingUp className="h-3 w-3"/> : <TrendingDown className="h-3 w-3"/>}
                <span>Variance</span>
              </div>
              <div className={`meter-display text-base font-extrabold mt-0.5 ${varianceTone}`} data-testid="drawer-bank-variance">
                {varianceRaw != null ? `${varianceRaw >= 0 ? "+" : ""}${fmtUsd(Math.abs(varianceRaw))}` : "—"}
              </div>
              <div className="text-[9px] text-zinc-500 mt-0.5">Bank − Expected</div>
            </div>
          </div>

          {/* Editor row · admin enters actual bank amount */}
          <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2 items-end">
            <div>
              <Label className="text-[9px] uppercase tracking-widest text-zinc-400 flex items-center gap-1">
                <Wallet className="h-3 w-3 text-purple-300"/>Bank Statement Amount
              </Label>
              <div className="relative mt-1">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-amber-400 text-xs font-bold pointer-events-none">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={bankAmt}
                  onChange={(e) => setBankAmt(e.target.value)}
                  className="bg-zinc-900 border-zinc-700 h-9 pl-5 meter-display text-xs"
                  data-testid="drawer-bank-amount-input"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <Label className="text-[9px] uppercase tracking-widest text-zinc-400 flex items-center gap-1">
                <MessageSquare className="h-3 w-3"/>Note (optional)
              </Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Bank ref# 20260705-991 · Cleared 07/06 12:14"
                className="bg-zinc-900 border-zinc-700 h-9 text-xs mt-1"
                data-testid="drawer-bank-note-input"
              />
            </div>
            <Button
              type="button"
              onClick={save}
              disabled={!canSave || saving}
              className="bg-purple-500 hover:bg-purple-400 text-zinc-950 font-extrabold h-9 mt-4 md:mt-0"
              data-testid="drawer-bank-save"
            >
              {saving ? "Saving…" : (diff?.bank_statement_amount != null ? "Update" : "Record")}
            </Button>
          </div>

          <div className="text-[10px] text-zinc-500 mt-2 leading-relaxed" data-testid="drawer-bank-recon-legend">
            Tolerance: <span className="text-emerald-300 font-bold">≤ ${diff.tolerance_green.toFixed(2)}</span> green ·
            {" "}<span className="text-amber-300 font-bold">≤ ${diff.tolerance_amber.toFixed(2)}</span> amber ·
            {" "}<span className="text-red-400 font-bold">&gt; ${diff.tolerance_amber.toFixed(2)}</span> red flag.
            Variance is signed: positive = bank received MORE than expected; negative = SHORTFALL vs ledger.
          </div>
        </>
      )}
    </div>
  );
}


function MiniColumn({ label, value, hint, tone, icon, testid }) {
  const toneClass = {
    sky:     "border-sky-400/40    bg-sky-500/5    text-sky-200",
    amber:   "border-amber-400/40  bg-amber-500/5  text-amber-200",
    purple:  "border-purple-400/40 bg-purple-500/5 text-purple-200",
    emerald: "border-emerald-400/40 bg-emerald-500/5 text-emerald-200",
  }[tone] || "border-zinc-700 bg-zinc-950 text-zinc-200";
  return (
    <div className={`p-2 rounded border ${toneClass}`} data-testid={`${testid}-tile`}>
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest opacity-80">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className="meter-display text-base font-extrabold mt-0.5" data-testid={testid}>
        {value}
      </div>
      <div className="text-[9px] opacity-70 mt-0.5 truncate">{hint}</div>
    </div>
  );
}


function MiniStat({ label, value, tone, testid }) {
  const t = tone === "amber" ? "text-amber-300"
    : tone === "red" ? "text-red-300"
    : "text-zinc-100";
  return (
    <div className="p-2 rounded bg-zinc-950 border border-zinc-800" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`meter-display font-bold text-base mt-0.5 ${t}`}>{value}</div>
    </div>
  );
}

function Field({ label, value, accent }) {
  const t = accent === "amber" ? "text-amber-300"
    : accent === "emerald" ? "text-emerald-300"
    : "text-zinc-200";
  return (
    <div className="p-1.5">
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`meter-display font-semibold mt-0.5 ${t}`}>{value ?? "—"}</div>
    </div>
  );
}
